import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useExpired } from "../context/ExpiredContext";
import { MdCreditCardOff } from "react-icons/md";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import {
  MdArrowForward,
  MdCalendarMonth,
  MdCastForEducation,
  MdHome,
  MdNotifications,
  MdPeople,
  MdRefresh,
  MdSignalCellular1Bar,
  MdStar,
} from "react-icons/md";
import { db } from "../../firebase/firebase-config";
import Colors from "../../assets/styles/Colors";

const cardSx = {
  background:
    "linear-gradient(145deg, rgba(20, 24, 33, 0.96), rgba(12, 15, 23, 0.98))",
  border: `1px solid ${Colors.borderSecondary}`,
  borderRadius: "18px",
  boxShadow: "0 18px 45px rgba(0, 0, 0, 0.22)",
};

const softCardSx = {
  ...cardSx,
  p: 3,
};

const iconWrapSx = {
  width: 42,
  height: 42,
  borderRadius: "12px",
  display: "grid",
  placeItems: "center",
  color: Colors.textPrimary,
  background: "rgba(137, 82, 255, 0.16)",
  border: "1px solid rgba(167, 139, 250, 0.25)",
  fontSize: 24,
};

const toArray = (snapshot) =>
  snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

const hasTag = (item, tag) => Array.isArray(item.tags) && item.tags.includes(tag);

const hasTagPrefix = (item, prefix) =>
  Array.isArray(item.tags) && item.tags.some((tag) => tag.startsWith(prefix));

const timestampValue = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const formatDate = (value) => {
  const millis = timestampValue(value);
  if (!millis) return "Sin fecha";
  return new Intl.DateTimeFormat("es-CR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(millis));
};

const isActiveSignal = (signal) => {
  const status = String(signal.status || "").toLowerCase();
  return status.includes("active") || status.includes("activa") || status.includes("open");
};

const getItemTitle = (item) =>
  item.name || item.title || item.alias || item.currency || item.email || "Sin titulo";

const loadDashboardData = async () => {
  const requests = {
    users: db.collection("users").get(),
    items: db.collection("academy_content").get(),
    sections: db.collection("sections").orderBy("order", "asc").get(),
    reviews: db.collection("reviews").get(),
    notifications: db.collection("notifications").get(),
    homeConfig: db.collection("home_config").get(),
    forexSignals: db.collection("signals").doc("forex").collection("items").get(),
    syntheticsSignals: db.collection("signals").doc("binaries").collection("items").get(),
    masterclass: db
      .collection("businesses")
      .doc("root")
      .collection("schedules")
      .doc("masterclass")
      .collection("events")
      .get(),
    sessions: db
      .collection("businesses")
      .doc("root")
      .collection("schedules")
      .doc("sessions")
      .collection("events")
      .get(),
  };

  const entries = await Promise.allSettled(
    Object.entries(requests).map(async ([key, promise]) => [key, await promise])
  );

  return entries.reduce(
    (acc, result) => {
      if (result.status === "fulfilled") {
        const [key, snapshot] = result.value;
        acc.data[key] = toArray(snapshot);
      } else {
        acc.errors.push(result.reason?.message || "No se pudo cargar una fuente de datos.");
      }
      return acc;
    },
    { data: {}, errors: [] }
  );
};

const StatCard = ({ icon, label, value, detail, to }) => (
  <Box sx={{ ...softCardSx, height: "100%" }}>
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2}>
      <Box sx={iconWrapSx}>{icon}</Box>
      {to ? (
        <Button
          component={RouterLink}
          to={to}
          size="small"
          endIcon={<MdArrowForward />}
          sx={{ color: Colors.textSecondary, textTransform: "none" }}
        >
          Abrir
        </Button>
      ) : null}
    </Stack>
    <Typography sx={{ mt: 3, color: Colors.textSecondary, fontWeight: 600 }}>
      {label}
    </Typography>
    <Typography sx={{ mt: 0.5, color: Colors.textPrimary, fontSize: 34, fontWeight: 800 }}>
      {value}
    </Typography>
    <Typography sx={{ color: Colors.textMuted, fontSize: 13 }}>{detail}</Typography>
  </Box>
);

const ActivityRow = ({ title, meta, to }) => (
  <Box
    component={to ? RouterLink : "div"}
    to={to || undefined}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 2,
      py: 1.4,
      borderBottom: `1px solid ${Colors.borderSecondary}`,
      color: "inherit",
      textDecoration: "none",
      "&:last-child": { borderBottom: 0 },
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography noWrap sx={{ color: Colors.textPrimary, fontWeight: 700 }}>
        {title}
      </Typography>
      <Typography noWrap sx={{ color: Colors.textMuted, fontSize: 13 }}>
        {meta}
      </Typography>
    </Box>
    {to ? <MdArrowForward color={Colors.textMuted} /> : null}
  </Box>
);

const SectionCard = ({ title, subtitle, children, actionTo }) => (
  <Box sx={{ ...softCardSx, height: "100%" }}>
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
      <Box>
        <Typography sx={{ color: Colors.textPrimary, fontSize: 18, fontWeight: 800 }}>
          {title}
        </Typography>
        <Typography sx={{ color: Colors.textMuted, fontSize: 13 }}>{subtitle}</Typography>
      </Box>
      {actionTo ? (
        <Button
          component={RouterLink}
          to={actionTo}
          size="small"
          sx={{ color: Colors.primary, textTransform: "none" }}
        >
          Gestionar
        </Button>
      ) : null}
    </Stack>
    <Box sx={{ mt: 2 }}>{children}</Box>
  </Box>
);

const chipColors = {
  mastermind: { bg: "#3b1a6b", color: "#c084fc" },
  trading:    { bg: "#0a2e1f", color: "#34d399" },
};

const SectionMiniCard = ({ section, courseCount, to }) => {
  const chip = chipColors[section.category] || { bg: Colors.bgTertiary, color: Colors.textPrimary };
  return (
    <Box
      component={RouterLink}
      to={to}
      sx={{
        display: "block",
        borderRadius: "12px",
        overflow: "hidden",
        border: `1px solid ${Colors.borderSecondary}`,
        background: Colors.bgTertiary,
        textDecoration: "none",
        transition: "border-color 0.18s, transform 0.18s",
        "&:hover": { borderColor: Colors.primary, transform: "translateY(-2px)" },
      }}
    >
      <Box sx={{ position: "relative", height: 90, background: section.gradient || `linear-gradient(135deg, ${Colors.bgTertiary}, ${Colors.bgElevated})`, overflow: "hidden" }}>
        {section.imageURL
          ? <img src={section.imageURL} alt={section.displayName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", opacity: 0.25, fontSize: 28 }}>
              {section.icon || "📚"}
            </Box>
        }
        <Chip
          label={section.category === "mastermind" ? "Mastermind" : "Trading"}
          size="small"
          sx={{ position: "absolute", top: 6, left: 6, backgroundColor: chip.bg, color: chip.color, fontWeight: 700, fontSize: "0.65rem" }}
        />
      </Box>
      <Box sx={{ p: 1.5 }}>
        <Typography sx={{ color: Colors.textPrimary, fontWeight: 700, fontSize: 13, lineHeight: 1.3 }} noWrap>
          {section.displayName}
        </Typography>
        <Typography sx={{ color: Colors.textMuted, fontSize: 11, mt: 0.25 }}>
          {courseCount} {courseCount === 1 ? "curso" : "cursos"}
        </Typography>
      </Box>
    </Box>
  );
};

const Dashboard = () => {
  const expired = useExpired();
  const [state, setState] = useState({
    loading: true,
    errors: [],
    data: {},
  });

  const fetchData = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, errors: [] }));
    const result = await loadDashboardData();
    setState({ loading: false, ...result });
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const summary = useMemo(() => {
    const users = state.data.users || [];
    const items = state.data.items || [];
    const reviews = state.data.reviews || [];
    const notifications = state.data.notifications || [];
    const homeConfig = state.data.homeConfig || [];
    const signals = [
      ...(state.data.forexSignals || []),
      ...(state.data.syntheticsSignals || []),
    ];
    const schedules = [
      ...(state.data.masterclass || []).map((item) => ({ ...item, type: "Masterclass" })),
      ...(state.data.sessions || []).map((item) => ({ ...item, type: "Sesión" })),
    ];

    const activeUsers = users.filter((user) => user.isDisabled !== true && user.isActive !== false);
    const goldUsers = users.filter((user) =>
      user.isGoldPlan === true ||
      ["pro", "elite"].includes(user.currentPlanKey)
    );
    const academyItems = items.filter((item) => hasTagPrefix(item, "education:"));
    const tradingItems = items.filter((item) => hasTagPrefix(item, "trading:"));
    const eventItems = items.filter((item) => item.category === "events");
    const recommendedItems = items.filter((item) => hasTag(item, "recommended"));
    const activeSignals = signals.filter(isActiveSignal);

    const recentItems = [...items]
      .sort((a, b) => timestampValue(b.cdate) - timestampValue(a.cdate))
      .slice(0, 5);

    const latestSignals = [...signals]
      .sort((a, b) => timestampValue(b.cdate || b.updated) - timestampValue(a.cdate || a.updated))
      .slice(0, 5);

    const scheduleRows = schedules
      .flatMap((event) =>
        Array.isArray(event.hours) && event.hours.length
          ? event.hours.map((hour) => ({
              id: `${event.id}-${hour.day}-${hour.hour}`,
              title: event.title,
              type: event.type,
              hour: hour.hour,
              day: hour.day,
            }))
          : [{ id: event.id, title: event.title, type: event.type, hour: "", day: "" }]
      )
      .slice(0, 6);

    const sections = state.data.sections || [];
    const mastermindSections = sections.filter((s) => s.category === "mastermind");
    const tradingSections    = sections.filter((s) => s.category === "trading");

    const courseCountByTag = items.reduce((acc, item) => {
      (item.tags || []).forEach((tag) => {
        acc[tag] = (acc[tag] || 0) + 1;
      });
      return acc;
    }, {});

    return {
      users,
      activeUsers,
      goldUsers,
      items,
      academyItems,
      tradingItems,
      eventItems,
      recommendedItems,
      reviews,
      notifications,
      homeConfig,
      signals,
      activeSignals,
      recentItems,
      latestSignals,
      scheduleRows,
      sections,
      mastermindSections,
      tradingSections,
      courseCountByTag,
    };
  }, [state.data]);

  return (
    <Box
      id="dashboard"
      sx={{
        alignItems: "stretch",
        minHeight: "100%",
        mt: 0,
        width: "100%",
      }}
    >
      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
        {expired && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              px: 2.5,
              py: 2,
              mb: 3,
              borderRadius: "12px",
              background: "linear-gradient(135deg, rgba(239,68,68,0.10) 0%, rgba(239,68,68,0.04) 100%)",
              border: "1px solid rgba(239,68,68,0.28)",
            }}
          >
            <MdCreditCardOff size={22} color="#EF4444" style={{ flexShrink: 0 }} />
            <Box>
              <Typography sx={{ color: "#EF4444", fontWeight: 700, fontSize: "0.88rem", lineHeight: 1.3 }}>
                Pago vencido — contactá a soporte
              </Typography>
              <Typography sx={{ color: "rgba(239,68,68,0.7)", fontSize: "0.78rem", mt: 0.3 }}>
                Procesá el pago pendiente para restablecer el acceso completo al panel.
              </Typography>
            </Box>
          </Box>
        )}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          gap={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography sx={{ color: Colors.textPrimary, fontSize: 30, fontWeight: 900 }}>
              Dashboard
            </Typography>
            <Typography sx={{ color: Colors.textSecondary }}>
              Resumen operativo de contenido, usuarios y actividad de NuCore.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={state.loading ? <CircularProgress size={16} color="inherit" /> : <MdRefresh />}
            onClick={fetchData}
            disabled={state.loading}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 700,
              background: "linear-gradient(135deg, #7c3aed, #a855f7)",
            }}
          >
            Actualizar
          </Button>
        </Stack>

        {state.loading ? <LinearProgress sx={{ mb: 3, borderRadius: 999 }} /> : null}

        {state.errors.length ? (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Algunos datos no pudieron cargarse. El dashboard muestra la información disponible.
          </Alert>
        ) : null}

        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              icon={<MdPeople />}
              label="Usuarios"
              value={summary.users.length}
              detail={`${summary.activeUsers.length} activos · ${summary.goldUsers.length} Elite`}
              to="/users"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              icon={<MdHome />}
              label="Contenido Home"
              value={summary.items.length}
              detail={`${summary.recommendedItems.length} recomendados · ${summary.eventItems.length} eventos`}
              to="/items"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              icon={<MdCastForEducation />}
              label="Academia"
              value={summary.academyItems.length}
              detail={`${summary.tradingItems.length} contenidos de trading`}
              to="/academy"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              icon={<MdSignalCellular1Bar />}
              label="Señales"
              value={summary.signals.length}
              detail={`${summary.activeSignals.length} activas entre forex y sintéticos`}
              to="/signals"
            />
          </Grid>

          <Grid item xs={12} lg={7}>
            <SectionCard
              title="Actividad de contenido"
              subtitle="Últimos items cargados en academy_content"
              actionTo="/items"
            >
              {summary.recentItems.length ? (
                summary.recentItems.map((item) => (
                  <ActivityRow
                    key={item.id}
                    title={getItemTitle(item)}
                    meta={`${item.category || "Sin categoria"} · ${formatDate(item.cdate)}`}
                    to="/items"
                  />
                ))
              ) : (
                <Typography sx={{ color: Colors.textMuted }}>No hay contenido registrado.</Typography>
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={5}>
            <SectionCard title="Estado del app" subtitle="Puntos clave para lanzamiento">
              <Stack spacing={1.4}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ color: Colors.textSecondary }}>Banners Inicio</Typography>
                  <Chip label={summary.homeConfig.length} size="small" color="primary" />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ color: Colors.textSecondary }}>Reseñas</Typography>
                  <Chip icon={<MdStar />} label={summary.reviews.length} size="small" />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ color: Colors.textSecondary }}>Notificaciones</Typography>
                  <Chip icon={<MdNotifications />} label={summary.notifications.length} size="small" />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ color: Colors.textSecondary }}>Eventos Home</Typography>
                  <Chip label={summary.eventItems.length} size="small" />
                </Stack>
              </Stack>
              <Stack direction="row" flexWrap="wrap" gap={1.2} sx={{ mt: 3 }}>
                <Button component={RouterLink} to="/home-config" variant="outlined" size="small">
                  Banners Inicio
                </Button>
                <Button component={RouterLink} to="/reviews" variant="outlined" size="small">
                  Reseñas
                </Button>
                <Button component={RouterLink} to="/notifications" variant="outlined" size="small">
                  Push
                </Button>
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard
              title="Señales recientes"
              subtitle="Forex y sintéticos"
              actionTo="/signals"
            >
              {summary.latestSignals.length ? (
                summary.latestSignals.map((signal) => (
                  <ActivityRow
                    key={`${signal.category || "signal"}-${signal.id}`}
                    title={getItemTitle(signal)}
                    meta={`${signal.category || "señal"} · ${signal.status || "Sin estado"}`}
                    to="/signals"
                  />
                ))
              ) : (
                <Typography sx={{ color: Colors.textMuted }}>No hay señales registradas.</Typography>
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard
              title="Agenda"
              subtitle="Masterclass y sesiones cargadas"
              actionTo="/schedules/masterclass"
            >
              {summary.scheduleRows.length ? (
                summary.scheduleRows.map((event) => (
                  <ActivityRow
                    key={event.id}
                    title={event.title || "Evento sin titulo"}
                    meta={`${event.type}${event.day ? ` · ${event.day}` : ""}${event.hour ? ` · ${event.hour}` : ""}`}
                    to="/schedules/masterclass"
                  />
                ))
              ) : (
                <Typography sx={{ color: Colors.textMuted }}>No hay eventos en agenda.</Typography>
              )}
            </SectionCard>
          </Grid>

          {/* NuMind — Mastermind sections */}
          {summary.mastermindSections.length > 0 && (
            <Grid item xs={12} md={6}>
              <SectionCard title="NuMind" subtitle="Secciones de Mastermind" actionTo="/sections">
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 1.5 }}>
                  {summary.mastermindSections.map((sec) => (
                    <SectionMiniCard
                      key={sec.id}
                      section={sec}
                      courseCount={summary.courseCountByTag[sec.firestoreTag] || 0}
                      to="/academy"
                    />
                  ))}
                </Box>
              </SectionCard>
            </Grid>
          )}

          {/* NuCore — Trading sections */}
          {summary.tradingSections.length > 0 && (
            <Grid item xs={12} md={6}>
              <SectionCard title="NuCore" subtitle="Secciones de Trading" actionTo="/sections">
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 1.5 }}>
                  {summary.tradingSections.map((sec) => (
                    <SectionMiniCard
                      key={sec.id}
                      section={sec}
                      courseCount={summary.courseCountByTag[sec.firestoreTag] || 0}
                      to="/academy"
                    />
                  ))}
                </Box>
              </SectionCard>
            </Grid>
          )}

          <Grid item xs={12}>
            <Box sx={{ ...softCardSx, p: { xs: 2, md: 3 } }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", md: "center" }}
                gap={2}
              >
                <Box>
                  <Typography sx={{ color: Colors.textPrimary, fontWeight: 800, fontSize: 18 }}>
                    Accesos rápidos
                  </Typography>
                  <Typography sx={{ color: Colors.textMuted, fontSize: 13 }}>
                    Rutas usadas con frecuencia para preparar contenido antes del lanzamiento.
                  </Typography>
                </Box>
                <Stack direction="row" flexWrap="wrap" gap={1.2}>
                  <Button component={RouterLink} to="/academy" variant="contained">
                    Academia
                  </Button>
                  <Button component={RouterLink} to="/community" variant="outlined">
                    Comunidad
                  </Button>
                  <Button component={RouterLink} to="/forex" variant="outlined">
                    Trading
                  </Button>
                  <Button component={RouterLink} to="/schedules/sessions" variant="outlined" startIcon={<MdCalendarMonth />}>
                    Sesiones
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Dashboard;
